if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends DrawerComponent {
      constructor() {
        super();

        this.classesToRemoveOnLoad = 'drawer--loading';
        this.drawerBody = this.querySelector(this.selector);
        this.currentVariant = null;
        this.isRebuildingGallery = false;
        this.initialLoadComplete = false;
      }

      get selector() {
        return '.quick-view__content';
      }

      get sourceSelector() {
        return '#MainProduct-quick-view__content';
      }

      get requiresBodyAppended() {
        return !(Shopify.designMode && this.closest('.section-group-overlay-quick-view'));
      }

      doesVariantMatchAltText(variant, mediaAlt) {
        if (!variant || !variant.title || variant.title === '') {
          return true;
        }
        
        if (!mediaAlt || mediaAlt.trim() === '') {
          return true;
        }
        
        const braceMatch = mediaAlt.match(/\{([^}]+)\}/);
        
        if (!braceMatch) {
          return true;
        }
        
        const braceContent = braceMatch[1];
        const variantRequirements = braceContent.split(',').map(req => req.trim());
        
        const currentVariantTitle = (variant.title || '').toLowerCase().trim();
        const currentOptions = [
          (variant.option1 || '').toLowerCase().trim(),
          (variant.option2 || '').toLowerCase().trim(),
          (variant.option3 || '').toLowerCase().trim()
        ].filter(opt => opt);
        
        const allVariantText = [currentVariantTitle, ...currentOptions].join(' ').toLowerCase();
        
        for (const requirement of variantRequirements) {
          const requirementParts = requirement.split(/\s*[\/|]\s*/).map(part => part.trim().toLowerCase());
          
          const allPartsMatch = requirementParts.every(part => {
            if (currentVariantTitle === part) return true;
            if (currentOptions.some(opt => opt === part)) return true;
            if (allVariantText.includes(part)) return true;
            return false;
          });
          
          if (allPartsMatch && requirementParts.length > 0) {
            return true;
          }
        }
        
        return false;
      }

      shouleBeShow() {
        const sectionId = this.getProductQuickViewSectionId();
        return typeof sectionId === 'string';
      }

      prepareToShow() {
        super.prepareToShow();
        this.quickview();
      }

      handleAfterShow() {
        super.handleAfterShow();
        document.dispatchEvent(
          new CustomEvent('quick-view:open', {
            detail: { productUrl: this.dataset.productUrl },
          })
        );
      }

      handleAfterHide() {
        super.handleAfterHide();
        const drawerContent = this.querySelector(this.selector);
        drawerContent.innerHTML = '';
        this.classList.add(this.classesToRemoveOnLoad);
        this.currentVariant = null;
        this.isRebuildingGallery = false;
        this.initialLoadComplete = false;
      }

      getProductQuickViewSectionId() {
        let sectionId = FoxTheme.QuickViewSectionId || false;

        if (!sectionId) {
          // Get section id from overlay groups.
          const productQuickView = document.querySelector('.section-group-overlay-quick-view');
          if (productQuickView) {
            sectionId = FoxTheme.utils.getSectionId(productQuickView);
          }

          // Cache for better performance.
          FoxTheme.QuickViewSectionId = sectionId;
        }

        return sectionId;
      }

      quickview() {
        const drawerContent = this.querySelector(this.selector);
        const sectionId = this.getProductQuickViewSectionId();
        const sectionUrl = `${this.dataset.productUrl.split('?')[0]}?section_id=${sectionId}`;
        
        fetch(sectionUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const productElement = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector(this.sourceSelector);

            this.setInnerHTML(drawerContent, productElement.content.cloneNode(true));

            // Cache the original media elements before any filtering occurs
            const thumbContainer = drawerContent.querySelector(`#Slider-Thumbnails-${sectionId}`) ||
                                  drawerContent.querySelector('#Slider-Thumbnails') ||
                                  drawerContent.querySelector('.product__thumbnails');
                                  
            const mediaContainer = drawerContent.querySelector(`#Slider-Gallery-${sectionId}`) ||
                                  drawerContent.querySelector('#Slider-Gallery') ||
                                  drawerContent.querySelector('.product__media-list');

            if (thumbContainer && mediaContainer) {
              this.allThumbItems = Array.from(thumbContainer.querySelectorAll('.product__thumbs-item'));
              this.allMediaItems = Array.from(mediaContainer.querySelectorAll('.product__media-item'));
            }
            
            // Initialize variant change listeners
            this.initVariantChangeListener();
            
            // Get the initial variant
            const productInfo = drawerContent.querySelector('product-info');
            if (productInfo) {
              const variantData = productInfo.querySelector('variant-selects [data-selected-variant]');
              if (variantData) {
                this.currentVariant = JSON.parse(variantData.innerHTML);
              }
            }
            
            // Wait until the swiper is initialized AND has a rendered height
            // before filtering. A fixed timeout fails for uncached images on
            // slow connections (swiper height is 0 when filter runs).
            this.waitForGalleryReady(() => {
              this.filterGalleryForVariant(this.currentVariant, true);
              this.initialLoadComplete = true;
            });
            
            FoxTheme.a11y.trapFocus(this, this.focusElement);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            setTimeout(() => {
              this.classList.remove(this.classesToRemoveOnLoad);
            }, 300);

            document.dispatchEvent(
              new CustomEvent('quick-view:loaded', {
                detail: { productUrl: this.dataset.productUrl },
              })
            );
          })
          .catch((e) => {
            console.error(e);
          });
      }

      /**
       * Polls (via rAF) until the media gallery swiper exists and has a
       * non-zero rendered height, then invokes `callback`. Falls back after
       * `maxMs` milliseconds so the UI is never permanently blocked.
       */
      waitForGalleryReady(callback, maxMs = 4000) {
        const drawerContent = this.querySelector(this.selector);
        const sectionId = this.getProductQuickViewSectionId();
        const deadline = Date.now() + maxMs;

        const check = () => {
          const viewer =
            drawerContent?.querySelector(`#GalleryViewer-${sectionId}`) ||
            drawerContent?.querySelector('#GalleryViewer');

          if (viewer?.swiper && viewer.offsetHeight > 0) {
            callback();
          } else if (Date.now() < deadline) {
            requestAnimationFrame(check);
          } else {
            // Timed out — run anyway so the filter is at least attempted
            callback();
          }
        };

        requestAnimationFrame(check);
      }

      initVariantChangeListener() {
        const drawerContent = this.querySelector(this.selector);
        const productInfo = drawerContent.querySelector('product-info');
        
        if (!productInfo) return;
        
        // Listen for variant changes
        this.variantChangeHandler = this.handleVariantChange.bind(this);
        
        // Use MutationObserver to detect variant changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'subtree') {
              const variantData = productInfo.querySelector('variant-selects [data-selected-variant]');
              if (variantData) {
                const newVariant = JSON.parse(variantData.innerHTML);
                if (this.currentVariant?.id !== newVariant?.id) {
                  this.currentVariant = newVariant;
                  // Filter the gallery for the new variant
                  setTimeout(() => {
                    this.filterGalleryForVariant(newVariant);
                  }, 200);
                }
              }
            }
          });
        });
        
        observer.observe(productInfo, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-selected-variant']
        });
        
        this.variantObserver = observer;
        
        // Also listen for the variant:changed event
        document.addEventListener('variant:changed', this.variantChangeHandler);
      }

      handleVariantChange(event) {
        const variant = event.detail.variant;
        if (variant && variant.id !== this.currentVariant?.id) {
          this.currentVariant = variant;
          setTimeout(() => {
            this.filterGalleryForVariant(variant);
          }, 200);
        }
      }

      filterGalleryForVariant(variant, isInitialLoad = false) {
        if (!variant || !variant.title) return;
        
        const drawerContent = this.querySelector(this.selector);
        if (!drawerContent) return;

        const sectionId = this.getProductQuickViewSectionId();
        const productInfo = drawerContent.querySelector('product-info');
        const isAltTextImageFeatureEnabled = productInfo?.dataset?.altTextImageFeature === 'true';

        const mediaGallery = drawerContent.querySelector(`#MediaGallery-${sectionId}`) ||
                             drawerContent.querySelector('#MediaGallery') ||
                             drawerContent.querySelector('media-gallery');

        // When feature is disabled: just navigate the gallery normally.
        if (!isAltTextImageFeatureEnabled) {
          if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
            mediaGallery.setActiveMedia(variant);
          }
          return;
        }

        // On initial load the theme already renders the correct image —
        // only filter thumbnails visually, don't manipulate Swiper at all.
        if (isInitialLoad) {
          this._applyThumbnailFilter(variant, drawerContent, sectionId);
          return;
        }

        // On variant change: filter thumbnails + navigate gallery via setActiveMedia.
        this._applyThumbnailFilter(variant, drawerContent, sectionId);

        // Navigate the main gallery to the first image matching this variant.
        // setActiveMedia is the theme's own safe method — it doesn't touch Swiper height.
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(variant);
        }
      }

      // Hides non-matching thumbnail slides using CSS collapse so Swiper never
      // sees a layout change and autoHeight is never re-triggered.
      _applyThumbnailFilter(variant, drawerContent, sectionId) {
        const thumbContainer = drawerContent.querySelector(`#Slider-Thumbnails-${sectionId}`) ||
                               drawerContent.querySelector('#Slider-Thumbnails') ||
                               drawerContent.querySelector('.product__thumbnails');
        if (!thumbContainer) return;

        // Use cached original items, excluding Swiper loop duplicates.
        const allThumbItems = this.allThumbItems ||
          Array.from(thumbContainer.querySelectorAll('.product__thumbs-item:not(.swiper-slide-duplicate)'));

        allThumbItems.forEach(item => {
          const mediaAlt = item.getAttribute('data-media-alt') || '';
          const mediaType = item.getAttribute('data-media-type') || 'image';
          const matches = mediaType !== 'image' || this.doesVariantMatchAltText(variant, mediaAlt);

          if (matches) {
            // Restore: clear all collapse styles
            item.style.maxHeight = '';
            item.style.minHeight = '';
            item.style.overflow = '';
            item.style.opacity = '';
            item.style.padding = '';
            item.style.margin = '';
            item.style.border = '';
            item.style.pointerEvents = '';
          } else {
            // Collapse in-place without touching Swiper.
            // max-height:0 + overflow:hidden removes visual space
            // but the DOM node stays so Swiper's slide count is unchanged.
            item.style.maxHeight = '0';
            item.style.minHeight = '0';
            item.style.overflow = 'hidden';
            item.style.opacity = '0';
            item.style.padding = '0';
            item.style.margin = '0';
            item.style.border = 'none';
            item.style.pointerEvents = 'none';
          }
        });
      }

      setInnerHTML(element, innerHTML) {
        element.innerHTML = '';
        element.appendChild(innerHTML);
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.handleAfterHide();
        
        // Clean up listeners
        if (this.variantObserver) {
          this.variantObserver.disconnect();
        }
        document.removeEventListener('variant:changed', this.variantChangeHandler);
      }
    }
  );
}